import DotMarker from "../../../images/dot.svg";
import { AutodeskViewer } from "../components/autodeskViewer";
import { IMarker, ISpaceOnMap, IVector } from "../interfaces";
import { Point } from "../models/point";

export class MarkerManager {
    private currentFloorLevel: number | undefined;
    public markersCleaned: boolean = false;

    public initialCameraPosition?: IVector = undefined;

    public constructor(private autodeskViewer: AutodeskViewer) {
        this.updateMarkers = this.updateMarkers.bind(this);
    }

    /**
     * Sets camera position
     * Used to determine the scale factor for markers when zooming in/out
     */
    public set cameraPosition(position: THREE.Vector3) {
        if (this.initialCameraPosition == null) {
            this.initialCameraPosition = {
                x: position.x,
                y: position.y,
                z: position.z,
            };
        }
    }

    public updateMarkers(payload: { markersUpdate: IMarker[]; floorLevel: number | undefined }): IMarker[] {

        this.currentFloorLevel = payload.floorLevel;

        this.cleanMarkers();

        if (payload.markersUpdate.length > 0) {
            this.addMarkers(payload.markersUpdate, payload.floorLevel);
        }

        this.markersCleaned = false;

        return [...payload.markersUpdate];
    }

    public findMarkerInList(searchMarker: IMarker, markers: IMarker[]): boolean {

        const index = markers.findIndex((marker) => {
            return marker.floorLevel === searchMarker.floorLevel &&
                marker.position?.x === searchMarker.position?.x &&
                marker.position?.y === searchMarker.position?.y &&
                marker.position?.z === searchMarker.position?.z &&
                marker.internalDbId === searchMarker.internalDbId &&
                marker.onClick === searchMarker.onClick &&
                marker.spaceId === searchMarker.spaceId
        });

        const hasBeenFound = index > -1;

        return hasBeenFound;
    }

    public getSpaceMarkers(spaces: ISpaceOnMap[]): IMarker[] {
        const markers =
            (spaces
                .filter((space) => space.style?.placeMarker != null)
                .map((space) => {
                    return { ...space.style?.placeMarker, position: space.position, internalDbId: space.dbId };
                }) as IMarker[]) ?? [];

        return markers;
    }

    public cleanMarkers(): void {
        const dataVizExt: any = this.autodeskViewer.visualizer;
        dataVizExt.removeAllViewables();
        this.markersCleaned = true;
    }

    public getMarkerAtPosition(mapPosition: THREE.Vector3 | undefined, availableMarkers: IMarker[], dbId: number): IMarker | undefined {

        if (!availableMarkers || availableMarkers.length === 0) {
            return undefined;
        }

        const foundMarker = availableMarkers.find((marker) => {

            const internalId = MarkerManager.getMarkerID(marker);

            return internalId === dbId;
        });

        return foundMarker;
    }

    /**
     * Init Data Visualization by adding a 1x1px marker
     *
     * @remark Initialization is necessary because only when a least one viewable is added
     *   to the canvas the Autodesk.DataVisualization.Core events, like MOUSE_CLICK, will work
     */
    public initDataVisualizationWithMarker(): void {
        const initializationMarker: IMarker = {
            internalDbId: -1,
            image: DotMarker,
            imageSize: 1,
            position: new Point(0, 0),
        };

        this.addMarkers([initializationMarker], undefined);
    }

    private async updateMarkerViewablesAsync(viewableData: Autodesk.DataVisualization.Core.ViewableData) {
        if (viewableData.viewables.length > 0) {
            await viewableData.finish();

            this.autodeskViewer.visualizer?.removeAllViewables();

            this.markersCleaned = true;

            this.autodeskViewer.visualizer?.addViewables(viewableData);
        }
    }

    public addMarkers(markers: IMarker[], floorLevel: number | undefined): void {
        const viewableData = new Autodesk.DataVisualization.Core.ViewableData();

        markers.forEach(async (marker: IMarker) => {

            const dbId = MarkerManager.getMarkerID(marker);
            const viewableStyle = new this.autodeskViewer.dataVizCore.ViewableStyle(
                this.autodeskViewer.dataVizCore.ViewableType.SPRITE,
                new THREE.Color(),
                marker.image
            );
            const viewable = new this.autodeskViewer.dataVizCore.SpriteViewable(Point.ToVector(marker.position!), viewableStyle, dbId);

            viewableData.spriteSize = marker.imageSize!;

            if (marker.image) {
                viewableData.addViewable(viewable);
            } 

            if (floorLevel != null && floorLevel !== this.currentFloorLevel) {
                this.removeMarkers([marker]);
            }
        });

        this.updateMarkerViewablesAsync(viewableData);
    }

    public removeMarkers(markers: IMarker[]): void {
        const dataVizExt: any = this.autodeskViewer.visualizer;
        markers.forEach((marker: IMarker) => {
            const dbId = MarkerManager.getMarkerID(marker);
            // a Mesh is a visualization of ViewableData. ViewableData can contain multiple items (e.g. markers)
            // A Mesh can be found by a dbId of an item/marker.
            // Removing the Mesh will remove the visualization of all items originally added to the ViewableData
            const mesh = dataVizExt.pointMeshes.find((mesh) => mesh.geometry.dbIds[0] === dbId);
            if (mesh) {
                this.autodeskViewer.viewer.overlays.removeMesh(mesh, "DataVizDots");
                dataVizExt.pointMeshes = dataVizExt.pointMeshes.filter((m) => m !== mesh);
                mesh.geometry.dispose();
                mesh.material.dispose();
            }
        });
    }

    public static getMarkerID(marker: IMarker): number {
        if (!marker.internalDbId) {
            marker.internalDbId = MarkerManager.getMarkerIDByPosition(marker.position);
        }
        return marker.internalDbId;
    }

    public static getMarkerIDByPosition(markerPosition?: Point): number {
        // sometimes Autodesk adds some more detail to coordinates
        //  so, rounding is necessary to use the coordinates for creating a marker id
        const x = MarkerManager.round(markerPosition?.x ?? 0);
        const y = MarkerManager.round(markerPosition?.y ?? 0);

        return MarkerManager.cantorPair(x, y);
    }

    /**
     * Combine to (positive) numbers into a unique number
     *
     * Classic Cantor Pairing Function
     * Works only on non-negative numbers.
     *
     * @remark By Steven Sell, https://jsfiddle.net/ssell/5smy3qg6/
     */
    private static cantorPair(x: number, y: number): number {
        return 0.5 * (x + y) * (x + y + 1) + y;
    }

    /**
     *
     * @param val Round a number to the specified number of decimals
     * @param numberOfDecimals
     * @returns
     *
     * @remark By Brian Ustas, https://stackoverflow.com/a/11832950
     */
    private static round(val: number, numberOfDecimals: number = 3): number {
        const rounding = Math.pow(10, numberOfDecimals);
        return Math.round((val + Number.EPSILON) * rounding) / rounding;
    }
}
